import { Box, Stack, Tab, useTheme } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { ExercisesList, IEditionsQueryData, LearningActivity, Tool } from "types";
import { TeachersContent } from "./TeachersContent.component";
import { handleFirstTab, handleTeachersAndTutors } from "./utils";
import { AttributeTabsCustom } from "../../components/FilterTabs/AttributeTabs.style";
import { ResourcesContent, TabsContentExercises } from "../../components/TabsContent";
import { Editions } from "../../components/TabsContent/Editions/Editions.component";
import { isEnrolledLoStatus, oneUserEdition } from "../../components/TabsContent/Editions/utils";
import { useRouteParamsLo } from "../../utils/useRouteParamsLo";

export const UtilityTabs = ({ 
  learningObject,
  tools, 
  exercises, 
  editionsQueryData
}: { 
  learningObject: LearningActivity 
  tools: Tool[],
   exercises: ExercisesList, 
   editionsQueryData?: IEditionsQueryData
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const {
    idString
  } = useRouteParamsLo();

  const [selectedTab, setSelectedTab] = useState(handleFirstTab(learningObject, tools, exercises));
  

  const allEditions = editionsQueryData?.editions ?? [];
  const enrolledEdition = allEditions.find((edition) => edition.status === "E");
  const staffEditions = enrolledEdition ? [enrolledEdition] : allEditions;
  const collectFromSessions = (key: "teachers" | "tutors") =>
    Array.from(
      new Map(
        staffEditions
          .flatMap((edition) => edition.sessions ?? [])
          .flatMap((session) => session[key] ?? [])
          .map((person) => [person.id, person])
      ).values()
    );

  const isClassroom = learningObject?.learningObjectTypology === "DA_PHYSICAL_CLASS"
    || learningObject?.learningObjectTypology === "DA_VIRTUAL_CLASS";
  const hasEditions = allEditions.length > 0;
  const isEnrolledInClassroom = hasEditions ? oneUserEdition(allEditions) : isEnrolledLoStatus(learningObject?.status);
  const hideStaffAndResources = isClassroom && !isEnrolledInClassroom;

  const teachers = hasEditions ? collectFromSessions("teachers") : (learningObject.teachers ?? []);
  const tutors = hasEditions ? collectFromSessions("tutors") : (learningObject.tutors ?? []);
  const teachersAndTutors = handleTeachersAndTutors(teachers, tutors, t);

  const a11yProps = (id: string) => {
    return {
      id: `virtual-classroom-tab-${id}`,
      "aria-controls": `virtual-classroom-tabpanel-${id}`
    };
  };

  const resourceTab = {
    label: TABS.RESOURCES,
    id: "resources",
    component: (
      <ResourcesContent 
        currentLO={ learningObject as LearningActivity }
        contentLinks={ learningObject.useful_links }
        contentTools={ tools }
      />
    )
    
  };
  const teachersTab = {
    label: TABS.TEACHERS,
    id: "teacher",
    component: (
      <TeachersContent teachers={ teachersAndTutors } />
    )
  };

  const exerciseTab = {
    label: TABS.EXERCISES,
    id: "exercises",
    component: (
      <TabsContentExercises
        teachers={ learningObject.teachers }
        exercises={ exercises ?? [] }
      />
    )
  };

  const editionTab = {
    label: TABS.EDITIONS,
    id: "editions",
    component: (
      <Editions
        learningObject={ learningObject as LearningActivity }
        isVirtual={ true }
        idString={ idString ?? "" }
        editionsQueryData={ editionsQueryData }
      />
    )
  };

  const filteredTabList = () => {
    // default tab is editions, other tabs get rendered only with datas available
    let tabList = [];

    if(learningObject?.learningObjectTypology === "DA_PHYSICAL_CLASS" || learningObject?.learningObjectTypology === "DA_VIRTUAL_CLASS") {
      tabList.push(editionTab);
    }

    if(!hideStaffAndResources && (learningObject?.useful_links?.length > 0 || (tools && tools.length > 0))) {
      tabList.push(resourceTab);
    }
    if(!hideStaffAndResources && learningObject?.teachers?.length > 0) {
      tabList.push(teachersTab);
    }
    if(exercises && exercises.length > 0) {
      tabList.push(exerciseTab);
    }

    return tabList;
  };

  const tabs = filteredTabList();
  const effectiveSelectedTab = tabs.some(t => t.id === selectedTab)
    ? selectedTab
    : (tabs[0]?.id ?? selectedTab);

  return (
    <Stack>
      <AttributeTabsCustom
        sx={ {
          paddingTop: "0",
          ".MuiTab-textColorPrimary:not(.Mui-selected)": { color:  theme.customColors.systemPrimary01 },
          ".Mui-selected": { color: theme.customColors.accentVariantA },
          fontFamily: "Inter"
        } }
        TabIndicatorProps={ {
          style: {
            background: theme.customColors.accentVariantA, borderRadius: "1.5rem"
          }
        } }
        variant="scrollable"
        scrollButtons={ true }
        allowScrollButtonsMobile
        value={ effectiveSelectedTab }
        aria-label="virtual classroom utility tabs">
        { tabs.map((tab: {label: string, id: string}, index) => (
          <Tab
            tabIndex={ 0 }
            aria-label={ t(tab.label, tab.label) }
            sx={ {
              textTransform: "none",
              fontSize: "0.875rem",
              letterSpacing: "0.0313em",
              fontWeight: 600,
              fontStyle: "semibold",
              height: "1.5rem",
              minWidth: "3rem",
              marginX:"0.35rem",
              lineHeight: "1.5rem",
              borderBottom: `2px solid ${theme.customColors.border}`,
              "&.Mui-focusVisible": {
                border: `1px solid ${theme.customColors.accentVariantA}`
              }
            } }
            key={ index }
            label={ t(tab.label, tab.label) }
            value={ tab.id }
            onClick={ () => setSelectedTab(tab.id) }
            { ...a11yProps(tab.id) }
          />
        )) }
      </AttributeTabsCustom>
      { tabs.map((tab) => (
        <div
          tabIndex={ 0 }
          aria-label={ tab.label }
          key={ tab.id.toString() }
          role="tabpanel"
          hidden={ tab.id !== effectiveSelectedTab }
          id={ `tab-tabpanel-${tab.id.toString()}` }
          aria-labelledby={ `tab-tab-${tab.id.toString()}` }
        >
          { effectiveSelectedTab === tab.id
          && (
            <Box>
              { tab.component }
            </Box>
          ) }
        </div>
      ))
      }
    </Stack>
  );
};

const TABS = {
  EDITIONS: "editions",
  RESOURCES: "resources",
  TEACHERS: "teachers",
  EXERCISES: "exercises"
};



